import { useState, useEffect, useCallback } from 'react';
import { labApi, hardwareApi } from '../../../../services/labApi';
import {
  deriveAtRiskOrders,
  deriveInventorySummary,
  calcInventoryHealthPct,
  calcFulfillmentPct,
  calcHealthScore,
  calcProductionPace,
  mapChemicalToInventoryItem,
} from '../kpiUtils';
import { formatHardwareLabel } from '../hardwareUtils';

const matchHardware = (list, keyword) =>
  (list || []).find((h) => h.name?.toLowerCase().includes(keyword)) || null;

const matchHardwareVersions = (list, name) =>
  (list || []).filter((h) => h.name?.toLowerCase() === name.toLowerCase());

const toHardwareStock = (hw) => {
  if (!hw) return null;
  const current_stock = Math.max(0, Number(hw.current_stock || 0));
  const min_quantity = Number(hw.min_quantity || 0);
  return {
    id: hw.id,
    name: hw.name,
    version_tag: hw.version_tag || null,
    label: formatHardwareLabel(hw),
    current_stock,
    min_quantity,
    isLowStock: current_stock <= min_quantity,
  };
};

const matchHwCapacity = (list, keyword) =>
  (list || []).find(
    (h) => String(h.hardware_id) === keyword || h.name?.toLowerCase().includes(keyword)
  ) || null;

const computeFullSetStock = (deviceTotal, shaker, pump) => {
  const parts = [
    { label: 'Device', value: deviceTotal },
    shaker ? { label: 'Shaker', value: shaker.current_stock } : null,
    pump ? { label: 'Pump', value: pump.current_stock } : null,
  ].filter(Boolean);

  if (parts.length < 3) return null;

  const limiting = parts.reduce((min, part) => (part.value < min.value ? part : min));
  return {
    current_stock: limiting.value,
    limiting_component: limiting.label,
    device_stock: deviceTotal,
    shaker_stock: shaker.current_stock,
    pump_stock: pump.current_stock,
  };
};

const buildDataModel = (dashboard, workLogs, inventoryHealth, bottleneck, hardwareList, hwCapacity, chemicals) => {
  const stats = dashboard?.stats || {};
  const teamProgress = dashboard?.teamProgress || { totalTarget: 0, totalAchieved: 0, percentage: 0 };
  const crewPerformance = dashboard?.crewPerformance || [];
  const upcomingOrders = dashboard?.upcomingOrders || [];

  // Clamp all numeric stats to >= 0
  const safeStats = {
    ...stats,
    testCapacity: Math.max(0, stats.testCapacity || 0),
    hardware_sets_capacity: Math.max(0, stats.hardware_sets_capacity || 0),
    totalReagents: Math.max(0, stats.totalReagents || 0),
    activeCrew: Math.max(0, stats.activeCrew || 0),
    pendingOrders: Math.max(0, stats.pendingOrders || 0),
    lowStockItems: Math.max(0, stats.lowStockItems || 0),
  };

  // At-risk orders (derived from upcoming_orders — no dedicated API)
  const atRiskOrders = deriveAtRiskOrders(upcomingOrders);
  const atRiskCount = atRiskOrders.length;
  const fulfillableCount = Math.max(0, upcomingOrders.length - atRiskCount);

  // Inventory
  const inventorySummary = inventoryHealth?.summary || deriveInventorySummary(safeStats);
  const inventoryItems = inventoryHealth?.items || [];
  const reagentInventoryItems = inventoryItems.filter((i) => i.category === 'reagent');
  const ecInventoryItems = inventoryItems.filter((i) => i.category === 'hardware');
  const chemicalInventoryItems = (chemicals || []).map(mapChemicalToInventoryItem);
  const inventoryHealthPct = calcInventoryHealthPct(inventorySummary);

  // Crew
  const totalCrew = crewPerformance.length || 1;
  const crewAttendancePct = Math.round((safeStats.activeCrew / totalCrew) * 100);

  // KPIs
  const fulfillmentPct = calcFulfillmentPct({ fulfillableCount, atRiskCount });
  const healthScore = calcHealthScore({
    productionPct: teamProgress.percentage,
    inventoryHealthPct,
    fulfillmentPct,
    crewAttendancePct,
  });

  // Bottleneck
  const limitingFactor = bottleneck?.limiting_factor || null;
  const reagentRunway = bottleneck?.reagent_runway_days || null;

  // Pace
  const pace = calcProductionPace(teamProgress.totalAchieved, teamProgress.totalTarget);

  // Work log grouping
  const logsByWorker = (workLogs || []).reduce((acc, log) => {
    const name = log.worker_name || 'Unknown';
    if (!acc[name]) acc[name] = [];
    acc[name].push(log);
    return acc;
  }, {});

  // Hardware current stock (from hardware table — same as Hardware Devices page)
  const deviceStockVersions = matchHardwareVersions(hardwareList, 'device')
    .map(toHardwareStock)
    .filter(Boolean)
    .sort((a, b) => (a.version_tag || '').localeCompare(b.version_tag || ''));
  const deviceStock = deviceStockVersions[0] || null;
  const shakerStock = toHardwareStock(matchHardware(hardwareList, 'shaker'));
  const pumpStock = toHardwareStock(matchHardware(hardwareList, 'pump'));
  const hardwareStockList = (hardwareList || []).map(toHardwareStock).filter(Boolean);

  const deviceStockTotal = deviceStockVersions.reduce((sum, v) => sum + v.current_stock, 0);
  safeStats.deviceStock = deviceStockTotal;
  const hwFullSetStock = computeFullSetStock(deviceStockTotal, shakerStock, pumpStock);

  // Device manufacturing capacity (from BOM components — real DB calculation)
  const hwIndividuals = hwCapacity?.individual_capacities || [];
  const hwFullSet = hwCapacity?.full_set_capacity || null;
  const deviceCap = matchHwCapacity(hwIndividuals, 'device') || matchHwCapacity(hwIndividuals, 'HW0001001');
  const shakerCap = matchHwCapacity(hwIndividuals, 'shaker') || matchHwCapacity(hwIndividuals, 'HW0001002');
  const pumpCap   = matchHwCapacity(hwIndividuals, 'pump')   || matchHwCapacity(hwIndividuals, 'HW0001003');

  if (deviceCap?.max_capacity != null) {
    safeStats.hardware_sets_capacity = Math.max(0, Number(deviceCap.max_capacity));
  }

  return {
    stats: safeStats,
    teamProgress,
    crewPerformance,
    upcomingOrders,
    workLogs: workLogs || [],
    logsByWorker,
    atRiskOrders,
    fulfillableCount,
    atRiskCount,
    inventoryHealth: inventoryHealth || null,
    inventorySummary,
    inventoryItems,
    reagentInventoryItems,
    ecInventoryItems,
    chemicalInventoryItems,
    inventoryHealthPct,
    crewAttendancePct,
    fulfillmentPct,
    healthScore,
    limitingFactor,
    reagentRunway,
    bottleneck: bottleneck || null,
    pace,
    // Hardware stock (current_stock from DB)
    deviceStock,
    deviceStockVersions,
    shakerStock,
    pumpStock,
    hardwareStockList,
    hwFullSetStock,
    hasHardwareStock: hardwareStockList.length > 0,
    // Device manufacturing capacity (BOM-based)
    hwCapacity: hwCapacity || null,
    hwIndividuals,
    hwFullSet,
    deviceCap,
    shakerCap,
    pumpCap,
    hasHwCapacity: hwCapacity != null,
    // Endpoint flags
    hasInventoryEndpoint: inventoryHealth !== null,
    hasBottleneckEndpoint: bottleneck !== null,
  };
};

export const useDashboardData = (selectedDate) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAll = useCallback(async () => {
    const date = selectedDate || new Date().toISOString().split('T')[0];
    setLoading(true);
    try {
      const [dashboard, workLogs, inventoryHealth, bottleneck, hardwareList, hwCapacity, chemicals] = await Promise.all([
        labApi.getLabDashboard(date),
        labApi.getWorkLogsByDate(date),
        labApi.getInventoryHealth(),
        labApi.getCapacityBottleneck(),
        hardwareApi.getHardware().catch(() => []),
        hardwareApi.getHardwareCapacity().catch(() => null),
        labApi.getChemicals().catch(() => []),
      ]);

      setData(buildDataModel(dashboard, workLogs, inventoryHealth, bottleneck, hardwareList, hwCapacity, chemicals));
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  return { data, loading, error, lastUpdated, refresh: fetchAll };
};
